import { BillingPlanDefinitions, Plan, SubscriptionType } from 'types/billing';
import * as datefns from 'date-fns';
import { useBrandStore } from 'stores';
import { BrandStatus } from 'types/stores/brand-store';
import {
  BillingStatus1,
  SubscriptionPlan,
  SubscriptionStatus,
} from '@innovationdepartment/proxima-sdk-axios';
import { getDaysRemaining } from 'utils/getDaysRemaining';

const getNextBillingDate = (
  startDate: Date,
  plan: SubscriptionPlan,
  referenceDate: Date = new Date()
) => {
  if (plan === 'EVERY_30_DAYS') {
    const daysDiff = datefns.differenceInDays(referenceDate, startDate);
    const cyclesPassed = Math.floor(daysDiff / 30) + 1;
    return datefns.addDays(startDate, cyclesPassed * 30);
  }

  const yearsDiff = datefns.differenceInYears(referenceDate, startDate);
  const cyclesPassed = yearsDiff + 1;
  return datefns.addYears(startDate, cyclesPassed);
};

const getPlanLabel = (plan: SubscriptionPlan | undefined, isCancelled: boolean): string => {
  let baseName = 'Proxima - Trial';

  if (plan === Plan.MonthByMonth) {
    baseName = 'Proxima - Monthly';
  } else if (plan === Plan.Annual) {
    baseName = 'Proxima - Yearly';
  }

  if (isCancelled) {
    return `${baseName} (Cancelled)`;
  }

  return baseName;
};

export const useBillingCurrentPlan = (subscription: SubscriptionType): BillingPlanDefinitions => {
  const { trialEnd, plan, status, billApprovalDate, subscriptionStatus } = subscription;
  const { brand } = useBrandStore();

  const today = new Date();
  const trialEndDate = new Date(trialEnd);
  const trialDaysLeft = getDaysRemaining(trialEndDate);
  const isPastTrial = trialDaysLeft < 0;

  const isPendingConfirmation =
    subscriptionStatus === SubscriptionStatus.Pending ||
    subscriptionStatus === SubscriptionStatus.Confirming;

  const isPendingCancellation = subscriptionStatus === SubscriptionStatus.CancelPending;
  const isCancelled = subscriptionStatus === SubscriptionStatus.Cancelled || isPendingCancellation;
  const isPaymentFailed =
    subscriptionStatus === SubscriptionStatus.Declined ||
    subscriptionStatus === SubscriptionStatus.Frozen;

  // Trial logic
  const isTrial =
    status === BillingStatus1.Trial || subscriptionStatus === SubscriptionStatus.TrialActive;

  const isMonthly = plan === Plan.MonthByMonth;
  const isYearly = plan === Plan.Annual;
  const isLocked = brand?.status === BrandStatus.Locked;

  const planName = getPlanLabel(plan, isCancelled);

  let remainingDays: number | undefined;

  const planIsPending =
    SubscriptionStatus.Confirming === subscriptionStatus ||
    SubscriptionStatus.TrialActive === subscriptionStatus;

  if (isCancelled && billApprovalDate && plan) {
    remainingDays = datefns.differenceInCalendarDays(
      getNextBillingDate(new Date(billApprovalDate), plan),
      today
    );
  }

  let isAccountStillActive: boolean = true;
  if (isTrial) isAccountStillActive = trialDaysLeft > 0 || planIsPending;
  if (isCancelled) isAccountStillActive = remainingDays !== undefined && remainingDays > 0;
  if (isLocked) isAccountStillActive = false;

  const hasPlan = Boolean(plan);
  const hasPlanAndStillOnTrial = hasPlan && isTrial;

  return {
    isManaged: Boolean(brand?.isManaged),
    isLocked,
    isPaymentFailed,
    isPastTrial,
    isCancelled,
    isTrial,
    isMonthly,
    isYearly,
    isPendingConfirmation,
    isPendingCancellation,
    planName,
    trialDaysLeft,
    trialEnd,
    plan,
    remainingDays,
    planIsPending,
    isAccountStillActive,
    hasPlanAndStillOnTrial,
  };
};
